import * as React from 'react';

import { ApiContext } from 'context/ApiContext';
import QueryParams from 'context/QueryParams';
import {
  MainChartContainer,
  NoDataContainer,
  SpendByChartContainer,
  Wrapper,
} from './Applications.styles';
import { ApplicationsContext } from './ApplicationsContext';
import groupBy from 'lodash/groupBy';
import uniq from 'lodash/uniq';
import { CONTRIBUTIONS, DISPLAY_BY } from 'utils/constants';
import {
  ICurrencyFormat,
  ISettingsResponse,
  ISelectOption,
  IHeadlinesResponse,
  IDisplayByType,
  IApplicationSpendItem,
} from 'utils/types';
import ChartOptions from './ChartOptions';
import orderBy from 'lodash/orderBy';
import moment from 'moment';
import { ApplicationChart } from './ApplicationChart';

const OTHERS = 'Others';
const EMPTY_APP = {
  applicationName: '',
  applicationType: '',
  businessOwner: '',
  contribution: '',
  groupName: '',
  spend: 0,
  headcount: 0,
  fte: 0,
};

interface ISpendByContainerProps {
  applications: IApplicationSpendItem[];
  chartOption: ISelectOption;
  contributions?: string;
  displayBy: IDisplayByType;
  downloadApplicationHeroData: () => Promise<any>;
  format: ICurrencyFormat;
  headlines: IHeadlinesResponse;
  hoveredApplication?: string;
  loading: boolean;
  handleExportToImage: (c: any, f: string) => void;
  settings?: ISettingsResponse;
  showSuccessNotification: () => void;
  updateDisplayBy: (displayBy: IDisplayByType) => void;
  onContributionChange: (c: string) => void;
  params?: { [key: string]: string | string[] };
  selectedGroup?: string;
  selectedApplication?: string;
  setParams: (p: any) => void;
  updateAppState: (p: any) => void;
}

interface ISpendByContainerState {
  categories: any[];
  series: any[];
}

class SpendByContainer extends React.Component<
  ISpendByContainerProps,
  ISpendByContainerState
> {
  state = {
    categories: [],
    series: [],
    spendBy: '',
  };

  chartRef = React.createRef<any>();

  onChartChange = (chartOption: ISelectOption) => {
    this.props.updateAppState({ chartOption });
  };

  onDisplayByChange = (displayBy: IDisplayByType) => {
    this.props.updateDisplayBy(displayBy);
  };

  isPersonnel = () => {
    return this.props.contributions === CONTRIBUTIONS.PERSONNEL;
  };

  handleExport = (val: any) => {
    const { showSuccessNotification } = this.props;
    if (val.value === 'png') {
      this.props.handleExportToImage(
        this.getChartInstance(),
        `TXR_application_summary-${moment().format()}`
      );
    }
    if (val.value === 'xlsx') {
      this.props.downloadApplicationHeroData().then(showSuccessNotification);
    }
  };

  getChartInstance = () => {
    return (
      this.chartRef && this.chartRef.current && this.chartRef.current.chart
    );
  };

  getItemDataValue = (item?: IApplicationSpendItem) => {
    const { displayBy } = this.props;
    if (displayBy === DISPLAY_BY.HEADCOUNT) {
      return item && item.headcount ? item.headcount : 0;
    }
    return item ? item.spend : 0;
  };

  getCategories = () => {
    const { applications, displayBy, hoveredApplication, selectedGroup } =
      this.props;

    const orderProp =
      displayBy === DISPLAY_BY.HEADCOUNT ? 'headcount' : 'spend';
    let categories: string[] = [];
    let filteredApplications = applications;
    let titleProp: keyof Pick<
      IApplicationSpendItem,
      'applicationName' | 'groupName'
    > = selectedGroup ? 'applicationName' : 'groupName';
    if (selectedGroup) {
      filteredApplications = applications.filter(
        (application) => application.groupName === selectedGroup
      );
    }
    const highlightedApplications = filteredApplications.filter(
      (application) => application.applicationName === hoveredApplication
    );
    let highlightedCategories: string[] = [];
    if (highlightedApplications) {
      highlightedCategories = highlightedApplications.map(
        (item) => item[titleProp]
      );
    }
    const groupedApps = groupBy(applications, titleProp);
    const aggregatedApps: IApplicationSpendItem[] = Object.keys(
      groupedApps
    ).map((appName: string) => {
      return groupedApps[appName].reduce((acc, app) => {
        return {
          ...app,
          spend: acc.spend + app.spend,
          headcount: acc.headcount + app.headcount,
        };
      }, EMPTY_APP);
    });
    const orderedItems = orderBy(aggregatedApps, orderProp, 'desc');

    categories = uniq(orderedItems.map((item) => item[titleProp]));
    if (categories.length > 20) {
      const topCategories = categories.splice(0, 19);
      return {
        categories: [...topCategories, OTHERS],
        highlightedCategories,
        others: categories,
      };
    }
    return { categories, highlightedCategories, others: [] };
  };

  getCategoryData = (category: string, contribution: string) => {
    const { applications, selectedGroup } = this.props;
    const groupedFilteredItems = groupBy(applications, 'contribution');
    const groupedByContribution = groupedFilteredItems[contribution];
    let titleProp: keyof Pick<
      IApplicationSpendItem,
      'applicationName' | 'groupName'
    > = selectedGroup ? 'applicationName' : 'groupName';

    const items = groupedByContribution.filter(
      (it) => it[titleProp] === category
    );
    return items.reduce(
      (acc: number, item) => acc + this.getItemDataValue(item),
      0
    );
  };

  getChartData = () => {
    const { applications, selectedGroup } = this.props;
    if (applications.length === 0) {
      return { categories: [], series: [] };
    }
    const groupedFilteredItems = groupBy(applications, 'contribution');
    const { categories, highlightedCategories, others } = this.getCategories();
    let titleProp: keyof Pick<
      IApplicationSpendItem,
      'applicationName' | 'groupName'
    > = selectedGroup ? 'applicationName' : 'groupName';
    const series = Object.keys(groupedFilteredItems).map((contribution) => {
      const groupedByContribution = groupedFilteredItems[contribution];
      return {
        name: contribution,
        data: categories.map((category) => {
          if (category === OTHERS) {
            return others.reduce((acc, otherCategory) => {
              const item = groupedByContribution.find(
                (it) => it[titleProp] === otherCategory
              );
              return acc + this.getItemDataValue(item);
            }, 0);
          }
          return this.getCategoryData(category, contribution);
        }),
      };
    });

    return {
      categories,
      highlightedCategories,
      series,
    };
  };

  onBarClick = (category?: string) => {
    const { selectedApplication, selectedGroup } = this.props;
    if (!selectedGroup) {
      this.props.updateAppState({
        selectedGroup: category,
        selectedApplication: undefined,
      });
    } else {
      let newSelectedApplication: string | undefined = category;
      if (category === selectedApplication) {
        newSelectedApplication = undefined;
      }
      this.props.updateAppState({
        selectedApplication: newSelectedApplication,
      });
    }
  };

  render() {
    const {
      applications,
      chartOption,
      contributions,
      displayBy,
      format,
      loading,
      selectedGroup,
      selectedApplication,
    } = this.props;
    const displayByOption = {
      label: displayBy,
      value: displayBy,
    };
    const isPersonnel = this.isPersonnel();
    const isNoData = applications.length === 0 && !loading;

    const { categories, highlightedCategories, series } = this.getChartData();
    return (
      <Wrapper>
        <ChartOptions
          contributions={contributions}
          onExport={this.handleExport}
          isPersonnel={isPersonnel}
          displayByOption={displayByOption}
          onDisplayByChange={this.onDisplayByChange}
          selectedChartOption={chartOption}
          onChartChange={this.onChartChange}
          onSpendContributionChange={(contributionsOption: ISelectOption) => {
            this.props.onContributionChange(contributionsOption.value);
          }}
          selectedL1={selectedGroup}
          selectedL2={selectedApplication}
          clearL1={() => {
            this.props.updateAppState({
              selectedGroup: undefined,
              selectedApplication: undefined,
            });
          }}
          clearL2={() => {
            this.props.updateAppState({ selectedApplication: undefined });
          }}
        />
        {isNoData ? (
          <NoDataContainer>
            <h4>No Data Available for this combination of filters</h4>
            <br />
            <h6>Please try to reset or change the filter options</h6>
          </NoDataContainer>
        ) : (
          <SpendByChartContainer>
            <MainChartContainer>
              <ApplicationChart
                categories={categories}
                highlightedCategories={highlightedCategories}
                selectedCategory={selectedApplication}
                format={format}
                displayType={displayBy}
                onClick={this.onBarClick}
                series={series}
                type={chartOption.value}
                chartRef={this.chartRef}
              />
            </MainChartContainer>
          </SpendByChartContainer>
        )}
      </Wrapper>
    );
  }
}

export default (props: any) => (
  <QueryParams>
    {({ params, setParams }) => (
      <ApiContext.Consumer>
        {({ format, settings }) => (
          <ApplicationsContext.Consumer>
            {({ hoveredApplication }) => (
              <SpendByContainer
                {...props}
                format={format}
                hoveredApplication={hoveredApplication}
                params={params}
                setParams={setParams}
                settings={settings}
              />
            )}
          </ApplicationsContext.Consumer>
        )}
      </ApiContext.Consumer>
    )}
  </QueryParams>
);
